import { useMemo, useState } from 'react';
import type { Currency, UnitCode } from '~/graphql/graphql';
import { roundTo6dp, type ClientError } from '~/utils';
import { toStoragePriceOrNull, type Dimensions } from '~/utils/unitConversion';

export type NegotiationSelectionTarget = {
  negotiationKey: string;
  sellingOrganizationId: string;
  organizationName: string;
  sellingOrganizationCode: string | null | undefined;
  currencyCode: Currency;
} & (
  | { kind: 'length'; platformProductLengthId: string }
  | { kind: 'tally'; tallyId: string }
);

export type NegotiationSelection = {
  selectedKeys: Set<string>;
  currency: Currency | null;
  onToggle: (target: NegotiationSelectionTarget) => void;
};

export const negotiationKey = (
  productId: string,
  sellingOrganizationId: string
) => [productId, sellingOrganizationId].join('-');

export function toggleNegotiationTarget(
  selected: Map<string, NegotiationSelectionTarget>,
  target: NegotiationSelectionTarget
): Map<string, NegotiationSelectionTarget> {
  const next = new Map(selected);
  if (!next.delete(target.negotiationKey)) {
    next.set(target.negotiationKey, target);
  }
  return next;
}

export function firstCurrency(
  targets: Iterable<NegotiationSelectionTarget>
): Currency | null {
  for (const target of targets) return target.currencyCode;
  return null;
}

export function targetsOfKind<K extends NegotiationSelectionTarget['kind']>(
  targets: NegotiationSelectionTarget[],
  kind: K
): Array<Extract<NegotiationSelectionTarget, { kind: K }>> {
  return targets.filter(
    (target): target is Extract<NegotiationSelectionTarget, { kind: K }> =>
      target.kind === kind
  );
}

export function uniqueSellerOrganizations(
  targets: readonly Pick<
    NegotiationSelectionTarget,
    'sellingOrganizationId' | 'organizationName' | 'sellingOrganizationCode'
  >[]
): {
  id: string;
  name: string;
  sellingOrganizationCode: string | null | undefined;
}[] {
  return Array.from(
    new Map(
      targets.map((target) => [
        target.sellingOrganizationId,
        {
          id: target.sellingOrganizationId,
          name: target.organizationName,
          sellingOrganizationCode: target.sellingOrganizationCode,
        },
      ])
    ).values()
  );
}

export function removeOrganization(
  selected: Map<string, NegotiationSelectionTarget>,
  organizationId: string
): Map<string, NegotiationSelectionTarget> {
  const remaining = [...selected].filter(
    ([, target]) => target.sellingOrganizationId !== organizationId
  );
  return remaining.length === selected.size ? selected : new Map(remaining);
}

export function useNegotiationSelection() {
  const [selectedTargets, setSelectedTargets] = useState<
    Map<string, NegotiationSelectionTarget>
  >(new Map());
  const selection: NegotiationSelection = useMemo(
    () => ({
      selectedKeys: new Set(selectedTargets.keys()),
      currency: firstCurrency(selectedTargets.values()),
      onToggle: (target) =>
        setSelectedTargets((previous) =>
          toggleNegotiationTarget(previous, target)
        ),
    }),
    [selectedTargets]
  );
  return { selection, selectedTargets, setSelectedTargets };
}

export function negotiateDisabledReason(
  targets: readonly Pick<NegotiationSelectionTarget, 'negotiationKey'>[],
  outstandingKeys: ReadonlySet<string>
): 'no_rows' | 'all_outstanding' | null {
  if (targets.length === 0) return 'no_rows';
  if (targets.every((target) => outstandingKeys.has(target.negotiationKey)))
    return 'all_outstanding';
  return null;
}

export function groupTargetsByPrice<T>(
  targets: T[],
  priceOf: (target: T) => number | null
): Map<number | null, T[]> {
  const targetsByPrice = new Map<number | null, T[]>();
  for (const target of targets) {
    const price = priceOf(target);
    const group = targetsByPrice.get(price) ?? [];
    group.push(target);
    targetsByPrice.set(price, group);
  }
  return targetsByPrice;
}

/** Per-target price restated per standard unit (null when blank or not convertible). */
export function storagePricesForTargets<
  T extends { platformProductLengthId: string },
>(
  targets: T[],
  targetPrice: number | null,
  preferredUnit: UnitCode,
  standardUnit: UnitCode,
  dimensionsOf: (platformProductLengthId: string) => Dimensions | null
): Map<T, number | null> {
  return new Map(
    targets.map((target) => {
      const dimensions = dimensionsOf(target.platformProductLengthId);
      const price =
        targetPrice == null || !dimensions
          ? null
          : toStoragePriceOrNull(
              targetPrice,
              preferredUnit,
              standardUnit,
              dimensions
            );
      return [target, price == null ? null : roundTo6dp(price)];
    })
  );
}

export function sendSerialPerSeller(
  requests: { sellerIds: string[]; send: () => Promise<unknown> }[]
): Promise<PromiseSettledResult<unknown>[]> {
  const lastBySeller = new Map<string, Promise<unknown>>();
  const all = requests.map(({ sellerIds, send }) => {
    const done = Promise.all(sellerIds.map((id) => lastBySeller.get(id))).then(
      send
    );
    sellerIds.forEach((id) => lastBySeller.set(id, done));
    return done;
  });
  return Promise.allSettled(all);
}

export function partitionNegotiationResults(
  priceGroups: readonly (readonly [
    unknown,
    readonly Pick<NegotiationSelectionTarget, 'sellingOrganizationId'>[],
  ])[],
  results: readonly PromiseSettledResult<unknown>[]
) {
  const failedSellerIds = new Set<string>();
  let firstReason!: ClientError;
  let rejected = 0;
  priceGroups.forEach(([, targets], index) => {
    const result = results[index];
    if (result.status !== 'rejected') return;
    if (rejected++ === 0) firstReason = result.reason;
    targets.forEach((target) =>
      failedSellerIds.add(target.sellingOrganizationId)
    );
  });
  return {
    failedSellerIds,
    firstReason,
    allRejected: rejected > 0 && rejected === results.length,
  };
}
